import axios, { AxiosError, type AxiosInstance } from 'axios';
import { auth } from './firebase';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Central API client. Every outbound request automatically attaches
 * the current user's Firebase ID token. The backend verifies it with
 * `firebase-admin` before any route runs.
 */
export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error: AxiosError<{ message?: string; code?: string }>) => {
    const status = error.response?.status;
    const message =
      error.response?.data?.message ??
      error.message ??
      'Unexpected error — please try again.';

    // Surface 401s so the AuthContext can force a re-auth.
    if (status === 401) {
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }

    return Promise.reject({ status, message, code: error.response?.data?.code });
  },
);

export type ApiError = {
  status?: number;
  message: string;
  code?: string;
};
